import { useState, useEffect } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { Layout } from "./components/layout/Layout";
import { Navbar } from "./components/layout/Navbar";
import { Footer } from "./components/layout/Footer";
import { BackgroundBoxes } from "./components/ui/background-boxes";
import { Loading } from "./components/ui/loading";
import { Dashboard } from "./components/features/dashboard/Dashboard";
import { About } from "./components/features/about/About";
import { useWalletAnalytics } from "./hooks/useWalletAnalytics";
import { useAnalytics } from "./lib/analytics";
import { ToastContainer } from "./components/ui/toast";
import { useToast } from "./hooks/useToast";

function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  return (
    <motion.div
      className="fixed top-0 left-0 right-0 h-1 bg-blue-500 origin-left z-50 pointer-events-none"
      style={{ scaleX }}
    />
  );
}

function App() {
  const [currentPage, setCurrentPage] = useState("dashboard");
  const [isLoading, setIsLoading] = useState(true);
  const analytics = useAnalytics();
  const { toasts, addToast, removeToast } = useToast();
  
  // Initialize wallet analytics
  useWalletAnalytics();

  // Track page views
  useEffect(() => {
    analytics.trackUserEvent('PAGE_VIEW', {
      page: currentPage,
      timestamp: Date.now(),
      sessionId: '',
    });
  }, [currentPage, analytics]);

  // Simulate initial loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="relative min-h-screen animated-bg flex flex-col">
      <Loading isLoading={isLoading} />
      
      {/* Fixed Background Layer */}
      <div className="fixed inset-0 z-0">
        <BackgroundBoxes className="!h-[150vh]">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/50 via-slate-950/75 to-slate-950/90" />
        </BackgroundBoxes>
        <div className="absolute inset-0 bg-grid opacity-20 pointer-events-none" />
      </div>
      
      {/* Content Layer */}
      <div className="relative z-10 flex flex-col min-h-screen">
        <Navbar currentPage={currentPage} setCurrentPage={setCurrentPage} />
        <Layout>
          <motion.div
            key={currentPage}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {currentPage === "dashboard" && <Dashboard />}
            {currentPage === "about" && <About />}
          </motion.div>
        </Layout>
        <Footer />
      </div>

      {/* Scroll Progress */}
      <ScrollProgress />

      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}

export default App;